import type { BillingPurchaseRequest, BillingPurchaseStatusResponse } from '@customerio/hydra-ui-api';
import type { Stripe } from '@stripe/stripe-js';
import { hydraUiApi } from '@/services/api';
import { getApiClientErrorStatus } from '@/services/api/errors';
import { accountStore } from '@/services/auth/store';
import { pollDelay, pollTask } from '@/utils/poll-task';
import { sentryCaptureException } from '@/utils/sentry';

// The poll covers the whole purchase, as on the Ordway rails.
const WALLET_PURCHASE_POLL_MAX_ATTEMPTS = 90;
const WALLET_PURCHASE_POLL_INTERVAL_MS = 2000;

/** Another purchase on this account is still running. */
export class WalletPurchaseInProgressError extends Error {
  constructor() {
    super(WALLET_PURCHASE_IN_PROGRESS_MESSAGE);
  }
}

/** The browser confirmed the payment, but the run had not finished when the poll gave up. */
export class WalletPurchaseTimedOutError extends Error {
  constructor() {
    super(WALLET_PURCHASE_TIMED_OUT_MESSAGE);
  }
}

/** Stripe did not confirm the payment, so the card was not charged. `errorType` is for analytics. */
export class PaymentNotChargedError extends Error {
  constructor(
    message: string,
    readonly errorType: 'card_error' | 'stripe_error',
  ) {
    super(message);
  }
}

/** Stripe.js never loaded, so the browser could not confirm the payment and the card was not charged. */
export class StripeNotLoadedError extends PaymentNotChargedError {
  constructor() {
    super(WALLET_PURCHASE_NOT_CHARGED_MESSAGE, 'stripe_error');
  }
}

export const WALLET_PURCHASE_IN_PROGRESS_MESSAGE = 'A purchase is already in progress. Try again after it completes.';
const WALLET_PURCHASE_DECLINED_MESSAGE = 'Your card was declined.';
const WALLET_PURCHASE_FAILED_MESSAGE = "Your purchase couldn't be processed. Contact support.";
const WALLET_PURCHASE_TIMED_OUT_MESSAGE = "Your payment is processing. Contact support if your balance doesn't update.";
// The run waits 15 minutes for the browser (`purchaseConfirmBudget` in services).
export const WALLET_PURCHASE_NOT_CHARGED_MESSAGE = "Your card wasn't charged. Try again in 15 minutes.";
// Before the confirmation, that wait has not started.
export const WALLET_PURCHASE_NOT_CHARGED_EARLY_MESSAGE = "Your card wasn't charged. Try again in about 20 minutes.";

export type WalletPurchaseRequest = BillingPurchaseRequest;

/**
 * Confirms a purchase's payment in the browser, including 3D Secure, when its run asks for it. Pass it
 * as the poll's `confirmPayment`. Throws {@link PaymentNotChargedError} when the card was not charged.
 *
 * Callers skip it in Stripe test mode. It does not import the Stripe Elements module, which loads
 * Stripe.js, because this module is part of the app-wide billing service.
 */
// The invoice already has the card, so send no payment method. An error result means no charge.
export async function confirmPurchasePayment(
  stripe: Pick<Stripe, 'confirmCardPayment'> | null,
  clientSecret: string,
): Promise<void> {
  // Without Stripe.js the browser cannot confirm, so nothing is charged.
  if (stripe == null) {
    throw new StripeNotLoadedError();
  }

  let result: Awaited<ReturnType<Stripe['confirmCardPayment']>>;
  try {
    result = await stripe.confirmCardPayment(clientSecret);
  } catch (error) {
    // The charge state is unknown. The run status decides it.
    sentryCaptureException(error);
    return;
  }

  if (result.error != null) {
    const isCardError = result.error.type === 'card_error';
    const reason = isCardError && result.error.message != null ? `${result.error.message} ` : '';
    throw new PaymentNotChargedError(
      `${reason}${WALLET_PURCHASE_NOT_CHARGED_MESSAGE}`,
      isCardError ? 'card_error' : 'stripe_error',
    );
  }
}

/**
 * Confirms a held charge's client secret unless `confirmedSecrets` already has it, then adds it. A
 * run keeps answering with the same secret until the charge settles, and asks for a new one when it
 * replaces the charge, so each poll confirms each secret once.
 */
export async function confirmSecretOnce(
  secret: string | null | undefined,
  confirmedSecrets: Set<string>,
  confirmPayment: (clientSecret: string) => Promise<void>,
): Promise<void> {
  if (secret == null || confirmedSecrets.has(secret)) {
    return;
  }
  confirmedSecrets.add(secret);
  await confirmPayment(secret);
}

/** Resolves with the run id. Poll it with {@link pollForWalletPurchaseTask}. */
export async function startWalletPurchase(body: WalletPurchaseRequest): Promise<string> {
  const account = accountStore.get();
  if (account == null) {
    throw new Error('Cannot add funds without an account');
  }

  try {
    const response = await hydraUiApi.billing.purchase({
      params: { path: { account_id: String(account.id) } },
      body,
    });
    return response.run_id;
  } catch (error) {
    if (getApiClientErrorStatus(error) === 409) {
      throw new WalletPurchaseInProgressError();
    }
    throw error;
  }
}

/** Resolves `true` on success and `false` when cancelled. Throws the message to show. */
async function walletPurchaseRunner(
  signal: AbortSignal,
  runId: string,
  confirmPayment: (clientSecret: string) => Promise<void>,
): Promise<boolean> {
  const account = accountStore.get();
  if (account == null) {
    // The run is live, so `false` would hide it.
    throw new Error(WALLET_PURCHASE_NOT_CHARGED_EARLY_MESSAGE);
  }
  const params = { path: { account_id: String(account.id), run_id: runId } };
  const confirmedSecrets = new Set<string>();

  for (let attempt = 0; attempt < WALLET_PURCHASE_POLL_MAX_ATTEMPTS; attempt++) {
    if (signal.aborted) {
      return false;
    }

    const response = await readStatus(params);
    if (response != null && (await settleStatus(response, confirmedSecrets, confirmPayment))) {
      return true;
    }

    await pollDelay(WALLET_PURCHASE_POLL_INTERVAL_MS, signal);
  }

  sentryCaptureException(
    new Error(`Wallet purchase run ${runId} did not finish after ${WALLET_PURCHASE_POLL_MAX_ATTEMPTS} status reads`),
  );
  // The browser makes the charge, so no confirmation means no charge.
  if (confirmedSecrets.size > 0) {
    throw new WalletPurchaseTimedOutError();
  }
  throw new Error(WALLET_PURCHASE_NOT_CHARGED_EARLY_MESSAGE);
}

/**
 * Acts on one status read. Resolves `true` when the purchase succeeded and `false` to keep polling.
 * Throws the message to show when it ended in failure. Confirms each client secret once.
 */
async function settleStatus(
  response: BillingPurchaseStatusResponse,
  confirmedSecrets: Set<string>,
  confirmPayment: (clientSecret: string) => Promise<void>,
): Promise<boolean> {
  switch (response.status) {
    case 'success':
      return true;
    case 'declined':
      throw new Error(response.message ?? WALLET_PURCHASE_DECLINED_MESSAGE);
    case 'failed':
      throw new Error(WALLET_PURCHASE_FAILED_MESSAGE);
    case 'requires_confirmation':
      await confirmSecretOnce(response.client_secret, confirmedSecrets, confirmPayment);
      return false;
    case 'pending':
      return false;
    default: {
      const exhaustive: never = response.status;
      throw new Error(String(exhaustive));
    }
  }
}

// One task per surface, because `cancelAll()` stops every run of a task. The Add funds modal cancels
// its task when it closes, and that must not stop a one-time purchase started on another page.
export const pollForWalletPurchaseTask = pollTask(walletPurchaseRunner);
export const pollForOneTimePurchaseTask = pollTask(walletPurchaseRunner);
export const pollForCardSaveTask = pollTask(walletPurchaseRunner);

// A 4xx other than 429 will not change, so it ends the poll.
async function readStatus(params: { path: { account_id: string; run_id: string } }) {
  try {
    return await hydraUiApi.billing.getPurchaseStatus({ params });
  } catch (error) {
    const status = getApiClientErrorStatus(error);
    if (status != null && status >= 400 && status < 500 && status !== 429) {
      sentryCaptureException(error);
      throw new Error(WALLET_PURCHASE_FAILED_MESSAGE);
    }
    return undefined;
  }
}
